// @ts-nocheck

import { GlobalAdminGuard } from "components/common/Guards/GlobalAdmin";
import dayjs from "dayjs";
import {
  Eye,
  RotateCcw,
  Settings,
  SquarePen,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext";
import {
  PLAN_CHESSBOARD_X as DEFAULT_PLAN_CHESSBOARD_X,
  PLAN_CHESSBOARD_Y as DEFAULT_PLAN_CHESSBOARD_Y,
} from "../../../mocks";
import api from "../../../utils/api";
import ConfirmModal from "../../material/ConfirmModal";
import CreateObjectModal from "./CreateObjectModal";

// Zoom configuration constants
const ZOOM_CONFIG = {
  MIN: 0.01,
  MAX: 3,
  BUTTON_IN_FACTOR: 0.2,
  BUTTON_OUT_FACTOR: -0.2,
  WHEEL_SENSITIVITY: 0.2,
  WHEEL_PIXEL_MODE_MULTIPLIER: 0.01,
  WHEEL_LINE_MODE_MULTIPLIER: 0.05,
  WHEEL_MAX_DELTA: 1,
};

const STATUSES = {
  installed: "installed",
  uninstalled: "uninstalled",
  real_vs_plan_dev: "real_vs_plan_dev",
  occluded: "occluded",
};

interface IProgressReportModalViewer {
  sectionData: any;
  projectId: string;
  onClose?: () => void;
  onOpenDocumentation?: () => void;
  selectedObjectId?: string | null;
  addedBboxInfo?: any;
  isObjectCreationMode?: boolean;
  onDoneObjectUpdate?: () => void;
  isViewMode?: boolean;
}

const getChessboardPiece = (
  clickX,
  clickY,
  col1,
  col2,
  row1,
  row2,
  PLAN_CHESSBOARD_X,
  PLAN_CHESSBOARD_Y,
) => {
  const x1 = PLAN_CHESSBOARD_X[col1];
  const x2 = PLAN_CHESSBOARD_X[col2];
  const y1 = PLAN_CHESSBOARD_Y[row1];
  const y2 = PLAN_CHESSBOARD_Y[row2];
  if ([x1, x2, y1, y2].some((v) => v === undefined)) return undefined;

  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const top = Math.min(y1, y2);
  const bottom = Math.max(y1, y2);

  const width = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return undefined;

  const xT1 = left + width / 3;
  const xT2 = left + (2 * width) / 3;
  const yT1 = top + height / 3;
  const yT2 = top + (2 * height) / 3;

  let horiz;
  if (clickX < xT1) horiz = "left";
  else if (clickX > xT2) horiz = "right";
  else horiz = "center";

  let vert;
  if (clickY < yT1) vert = "upper";
  else if (clickY > yT2) vert = "lower";
  else vert = "center";

  if (vert === "center" && horiz === "center") return "center";
  if (vert === "upper" && horiz === "left") return "upper left corner";
  if (vert === "upper" && horiz === "right") return "upper right corner";
  if (vert === "lower" && horiz === "left") return "lower left corner";
  if (vert === "lower" && horiz === "right") return "lower right corner";
  if (vert === "upper" && horiz === "center") return "upper center";
  if (vert === "lower" && horiz === "center") return "lower center";
  if (vert === "center" && horiz === "left") return "center left";
  if (vert === "center" && horiz === "right") return "center right";
  return `${vert} ${horiz}`;
};

const ProgressReportModalViewer = ({
  sectionData,
  projectId,
  onClose,
  onOpenDocumentation,
  selectedObjectId,
  addedBboxInfo,
  isObjectCreationMode,
  onDoneObjectUpdate,
  isViewMode,
}: IProgressReportModalViewer) => {
  const { t } = useTranslation();
  const [detectedObjects, setDetectedObjects] = useState([]);
  const [showObjectLabels, setShowObjectLabels] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeletingFloorPlanElement, setIsDeletingFloorPlanElement] =
    useState(false);
  const [deleteFloorPlanElementModalOpen, setDeleteFloorPlanElementModalOpen] =
    useState(false);
  const [isDeletingCable, setIsDeletingCable] = useState(false);
  const [deleteCableModalOpen, setDeleteCableModalOpen] = useState(false);
  const [selectedCableForDeletion, setSelectedCableForDeletion] =
    useState<any>(null);
  const [error, setError] = useState(null);
  const [planImageLoaded, setPlanImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHoveringObject, setIsHoveringObject] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { user } = useAuth();
  const [createObjectModal, setCreateObjectModal] = useState({
    isOpen: false,
    data: null,
  });
  const [isMergingMode, setIsMergingMode] = useState(false);
  const [cableInstallModal, setCableInstallModal] = useState({
    isOpen: false,
    status: null,
  });
  const dragStartCoords = useRef({ x: 0, y: 0 });

  // Mobile touch states
  const [isTouching, setIsTouching] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);
  const touchStartCoords = useRef({ x: 0, y: 0 });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [filteredObjectIds, setFilteredObjectIds] = useState([]);

  const flashIntervalRef = useRef(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [shouldFlash, setShouldFlash] = useState(false);

  const canvasRef = useRef(null);
  const planImageRef = useRef(null);
  const containerRef = useRef(null);
  const mountDrawStateRef = useRef("initial");

  const PLAN_CHESSBOARD_X = useMemo(
    () => sectionData?.plan?.chessboard?.x || DEFAULT_PLAN_CHESSBOARD_X,
    [sectionData],
  );
  const PLAN_CHESSBOARD_Y = useMemo(
    () => sectionData?.plan?.chessboard?.y || DEFAULT_PLAN_CHESSBOARD_Y,
    [sectionData],
  );

  const selectedObject = useMemo(() => {
    return selectedObjects[selectedObjects.length - 1];
  }, [selectedObjects]);

  const filteredObjects = useMemo(() => {
    return detectedObjects
      .filter((obj) => selectedStatuses.includes(obj.status))
      .filter((obj) => selectedTypes.includes(obj.group_id));
  }, [detectedObjects, selectedStatuses, selectedTypes]);

  const zoomToObject = useCallback((obj) => {
    if (!obj || !containerRef.current || !planImageRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    // Calculate object center
    const objectCenterX = obj.position.x + 40 / 2;
    const objectCenterY = obj.position.y + 40 / 2;

    // Set zoom level to focus on object (adjust this value as needed)
    const targetZoom = Math.min(1.5, Math.max(0.8, 1.2));

    // Calculate pan offset to center the object in the viewport
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;

    const newPanX = containerCenterX - objectCenterX * targetZoom;
    const newPanY = containerCenterY - objectCenterY * targetZoom;

    setZoomLevel(targetZoom);
    setPanOffset({ x: newPanX, y: newPanY });
  }, []);

  useEffect(() => {
    if (selectedObjectId && detectedObjects.length > 0) {
      const _selectedObject = detectedObjects.find(
        (obj) => obj._id === selectedObjectId,
      );
      if (_selectedObject) {
        setSelectedObjects([_selectedObject]);
      }
      if (isViewMode) {
        setFilteredObjectIds([selectedObjectId]);
      }
    }
  }, [detectedObjects, selectedObjectId, isViewMode]);

  // Auto-zoom to selected object when selectedObjectId is provided
  const isZoomedToObject = useRef(false);
  useEffect(() => {
    if (
      selectedObjectId &&
      selectedObject &&
      planImageLoaded &&
      !isZoomedToObject.current
    ) {
      // Add a small delay to ensure the image and canvas are properly rendered
      const timeoutId = setTimeout(() => {
        zoomToObject(selectedObject);
        isZoomedToObject.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedObject, selectedObjectId, planImageLoaded, zoomToObject]);

  // Auto-zoom to addedBboxInfo coordinates when provided
  const isZoomedToBbox = useRef(false);
  useEffect(() => {
    if (
      addedBboxInfo &&
      addedBboxInfo.zoomX !== undefined &&
      addedBboxInfo.zoomY !== undefined &&
      planImageLoaded &&
      !isZoomedToBbox.current
    ) {
      const timeoutId = setTimeout(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        // Set zoom level for bbox focus
        const targetZoom = Math.min(1.5, Math.max(0.8, 1.2));

        // Calculate pan offset to center the bbox coordinates in the viewport
        const containerCenterX = containerRect.width / 2;
        const containerCenterY = containerRect.height / 2;

        const newPanX = containerCenterX - addedBboxInfo.zoomX * targetZoom;
        const newPanY = containerCenterY - addedBboxInfo.zoomY * targetZoom;

        setZoomLevel(targetZoom);
        setPanOffset({ x: newPanX, y: newPanY });
        isZoomedToBbox.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [addedBboxInfo, planImageLoaded]);

  useEffect(() => {
    const setZoomToFit = () => {
      const container = containerRef.current;
      const img = planImageRef?.current;
      if (container && img) {
        const containerRect = container.getBoundingClientRect();
        const imgNaturalWidth = img.naturalWidth;
        const imgNaturalHeight = img.naturalHeight;
        if (imgNaturalWidth && imgNaturalHeight) {
          const scaleX = containerRect.width / imgNaturalWidth;
          const scaleY = containerRect.height / imgNaturalHeight;
          const fitZoom = Math.min(scaleX, scaleY, 1);
          setZoomLevel(fitZoom);
          setPanOffset({ x: 0, y: 0 });
        }
      }
    };

    const imageElement = planImageRef.current;
    if (imageElement && imageElement.complete) {
      setZoomToFit();
    } else if (imageElement) {
      imageElement.onload = setZoomToFit;
    }
    return () => {
      if (imageElement) {
        imageElement.onload = null;
      }
    };
  }, [containerRef]);

  // Size canvas to container (not image) to avoid huge canvas allocations
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();

    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(container);

    return () => observer.disconnect();
  }, [planImageLoaded]);

  const drawObjectsOnCanvas = useCallback(
    ({ visible = true, objects = [], isFlashMode = false }) => {
      const canvas = canvasRef.current;
      const planImage = planImageRef.current;

      if (!canvas || !planImage) return;

      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!visible) {
        return;
      }

      // Apply DPR scaling, then pan/zoom to map image-space coords to container-space
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.translate(panOffset.x, panOffset.y);
      ctx.scale(zoomLevel, zoomLevel);

      // Draw detected objects
      const _obj = objects.find((obj) => obj.id === "obj_none_629");

      objects.forEach((obj) => {
        const x = obj.position.x;
        const y = obj.position.y;
        const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
        const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;

        // const color = getObjectColor(obj.status);
        let color = `rgb(${obj.dwg_rgb[0]}, ${obj.dwg_rgb[1]}, ${obj.dwg_rgb[2]})`;
        if (isObjectCreationMode) {
          if (obj.status === "installed") {
            color = "green";
          } else if (obj.status === "uninstalled") {
            color = "orange";
          } else if (obj.status === "plan_vs_real_dev") {
            color = "red";
          } else if (obj.status === "real_vs_plan_dev") {
            color = "red";
          } else if (obj.status === "occluded") {
            color = "amber";
          }
        }

        const isSelected = selectedObjects.some(
          (object) => object._id === obj._id,
        );

        ctx.beginPath();
        ctx.arc(x, y, Math.max(width, height) / 2, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.globalAlpha =
          (isFlashMode || zoomLevel < 0.4) && obj.status !== "uninstalled"
            ? 1
            : 0.2;
        ctx.fill();
        ctx.globalAlpha = 1;

        // Draw border if selected
        if (isSelected) {
          ctx.lineWidth = 3 / zoomLevel;
          ctx.strokeStyle = "#000000"; // black border for selection
          ctx.setLineDash([5, 5]);
          ctx.stroke();
          ctx.setLineDash([]);
        } else if (!(isFlashMode || zoomLevel < 0.4)) {
          ctx.lineWidth = 1;
          ctx.strokeStyle = color; // black border for selection
          ctx.setLineDash([]); // solid border
          ctx.stroke();
        }

        // Draw label if enabled
        if (showObjectLabels) {
          ctx.fillStyle = "#1F2937";
          ctx.font = "12px sans-serif";
          ctx.fillText(
            `${obj.type} (${Math.round(obj.confidence * 100)}%)`,
            x,
            y - 5,
          );
        }
      });

      ctx.restore();
    },
    [zoomLevel, panOffset, isObjectCreationMode, selectedObjects, showObjectLabels],
  );

  const getBracketKeys = (mapping, value) => {
    const entries = Object.entries(mapping).sort(
      (a, b) => (a[1] as number) - (b[1] as number),
    );
    if (entries.length < 2) return [entries[0]?.[0], entries[0]?.[0]];
    if (value <= (entries[0][1] as number))
      return [entries[0][0], entries[1][0]];
    if (value >= (entries[entries.length - 1][1] as number))
      return [entries[entries.length - 2][0], entries[entries.length - 1][0]];
    for (let i = 0; i < entries.length - 1; i += 1) {
      const v1 = entries[i][1] as number;
      const v2 = entries[i + 1][1] as number;
      if (value >= v1 && value <= v2) return [entries[i][0], entries[i + 1][0]];
    }
    return [entries[0][0], entries[1][0]];
  };

  useEffect(() => {
    // Load detected objects for this section
    const loadDetectedObjects = async () => {
      if (!sectionData?._id || !projectId) {
        return;
      }

      if (!addedBboxInfo?.documentationId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.fetchDetectedObjectsByDocumentation(
          addedBboxInfo.documentationId,
        );

        if (response.success) {
          setDetectedObjects(response.data || []);
          console.log(
            `Loaded ${response.data?.length || 0} detected objects for section ${sectionData._id}`,
          );
        } else {
          throw new Error(
            response.message || "Failed to fetch detected objects",
          );
        }
      } catch (error) {
        console.error("Error loading detected objects:", error);
        setError(error.message || "Failed to load detected objects");
        setDetectedObjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadDetectedObjects();
  }, [sectionData?._id, projectId, addedBboxInfo?.documentationId]);

  const handleClearInProgressDraw = useCallback(() => {
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      mountDrawStateRef.current = "finished";
      setIsFlashing(false);
    }
  }, []);

  useEffect(() => {
    if (
      planImageLoaded &&
      detectedObjects.length > 0 &&
      (mountDrawStateRef.current === "finished" ||
        isObjectCreationMode ||
        isViewMode)
    ) {
      const filteredObjects = detectedObjects
        .filter((obj) => selectedStatuses.includes(obj.status))
        .filter((obj) => selectedTypes.includes(obj.group_id))
        .filter(
          (obj) =>
            filteredObjectIds.length === 0 ||
            filteredObjectIds.includes(obj._id),
        );

      if (shouldFlash && !isObjectCreationMode && !isViewMode) {
        let visible = true;
        let flashes = 0;
        const maxFlashes = 4;
        setIsFlashing(true);
        drawObjectsOnCanvas({
          visible: true,
          objects: filteredObjects,
          isFlashMode: true,
        });
        flashIntervalRef.current = setInterval(() => {
          visible = !visible;
          drawObjectsOnCanvas({
            visible,
            objects: filteredObjects,
            isFlashMode: true,
          });
          flashes++;
          if (flashes >= maxFlashes) {
            clearInterval(flashIntervalRef.current);
            drawObjectsOnCanvas({
              visible: true,
              objects: filteredObjects,
              isFlashMode: false,
            });
            setIsFlashing(false);
            setShouldFlash(false);
          }
        }, 200);
      } else {
        drawObjectsOnCanvas({ visible: true, objects: filteredObjects });
      }

      return () => {
        if (flashIntervalRef.current) {
          clearInterval(flashIntervalRef.current);
        }
      };
    }
  }, [
    detectedObjects,
    planImageLoaded,
    selectedStatuses,
    selectedTypes,
    filteredObjectIds,
    shouldFlash,
    isObjectCreationMode,
    drawObjectsOnCanvas,
    isViewMode,
  ]);

  // Add flash effect on mount also
  useEffect(() => {
    if (isViewMode) return;
    if (planImageLoaded && detectedObjects.length > 0) {
      if (mountDrawStateRef.current === "finished") return;
      mountDrawStateRef.current = "in_progress";
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
      }
      if (isObjectCreationMode) {
        drawObjectsOnCanvas({
          visible: true,
          objects: detectedObjects,
          isFlashMode: false,
        });
      } else {
        let visible = true;
        let flashes = 0;
        const maxFlashes = 6;
        setIsFlashing(true);
        drawObjectsOnCanvas({
          visible: true,
          objects: detectedObjects,
          isFlashMode: true,
        });
        flashIntervalRef.current = setInterval(() => {
          visible = !visible;
          drawObjectsOnCanvas({
            visible,
            objects: detectedObjects,
            isFlashMode: true,
          });
          flashes++;
          if (flashes >= maxFlashes) {
            clearInterval(flashIntervalRef.current);
            drawObjectsOnCanvas({
              visible: true,
              objects: detectedObjects,
              isFlashMode: false,
            });
            setIsFlashing(false);
            mountDrawStateRef.current = "finished";
          }
        }, 200);
      }
    }
    return () => {
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedObjects, isObjectCreationMode, planImageLoaded]);

  const handleObjectClick = useCallback(
    (event) => {
      if (isFlashing) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let clickX = event.clientX - containerRect.left;
      let clickY = event.clientY - containerRect.top;

      // Adjust click coordinates for canvas transforms (pan and zoom)
      clickX = (clickX - panOffset.x) / zoomLevel;
      clickY = (clickY - panOffset.y) / zoomLevel;

      // Find clicked object
      const clickedObject = detectedObjects.find((obj) => {
        const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
        const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;

        const x = obj.position.x - width / 2;
        const y = obj.position.y - height / 2;

        const isInBounds =
          clickX >= x &&
          clickX <= x + width &&
          clickY >= y &&
          clickY <= y + height;

        return isInBounds;
      });

      if (!clickedObject && isEditMode) {
        const detectionDate = new Date().toISOString();
        const [col1, col2] = getBracketKeys(PLAN_CHESSBOARD_X, clickX);
        const [row1, row2] = getBracketKeys(PLAN_CHESSBOARD_Y, clickY);

        const position_description = getChessboardPiece(
          clickX,
          clickY,
          col1,
          col2,
          row1,
          row2,
          PLAN_CHESSBOARD_X,
          PLAN_CHESSBOARD_Y,
        );

        setCreateObjectModal({
          isOpen: true,
          data: {
            position: { x: clickX, y: clickY },
            detectionDate,
            chessBoard: { grid_cols: [col1, col2], grid_rows: [row1, row2] },
            position_description,
          },
        });
        return;
      }
      if (clickedObject) {
        setSelectedObjects((prev) =>
          isMergingMode
            ? prev.includes(clickedObject)
              ? prev.filter((obj) => obj !== clickedObject)
              : [...prev, clickedObject]
            : [clickedObject],
        );
      } else {
        if (!isMergingMode) {
          setSelectedObjects([]);
        }
      }
    },
    [
      isFlashing,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      detectedObjects,
      isEditMode,
      PLAN_CHESSBOARD_X,
      PLAN_CHESSBOARD_Y,
      isMergingMode,
    ],
  );

  const handleZoom = useCallback(
    (directionOrEvent, mousePosition = null) => {
      let delta;
      let zoomPoint = mousePosition;

      if (typeof directionOrEvent === "string") {
        delta =
          directionOrEvent === "in"
            ? ZOOM_CONFIG.BUTTON_IN_FACTOR
            : ZOOM_CONFIG.BUTTON_OUT_FACTOR;
      } else if (directionOrEvent && directionOrEvent.deltaY !== undefined) {
        const event = directionOrEvent;
        delta = event.deltaY;

        if (event.deltaMode === 0) {
          delta *= ZOOM_CONFIG.WHEEL_PIXEL_MODE_MULTIPLIER;
        } else if (event.deltaMode === 1) {
          delta *= ZOOM_CONFIG.WHEEL_LINE_MODE_MULTIPLIER;
        }

        delta = Math.max(
          -ZOOM_CONFIG.WHEEL_MAX_DELTA,
          Math.min(ZOOM_CONFIG.WHEEL_MAX_DELTA, delta),
        );

        delta = -delta;

        if (!zoomPoint) {
          zoomPoint = {
            x: event.clientX,
            y: event.clientY,
          };
        }
      } else {
        return;
      }

      const factor = Math.exp(delta * ZOOM_CONFIG.WHEEL_SENSITIVITY);
      const newZoom = zoomLevel * factor;
      const clampedZoom = Math.max(
        ZOOM_CONFIG.MIN,
        Math.min(ZOOM_CONFIG.MAX, newZoom),
      );

      const containerRect = containerRef.current.getBoundingClientRect();
      const mx = zoomPoint
        ? zoomPoint.x - containerRect.left
        : containerRect.width / 2;
      const my = zoomPoint
        ? zoomPoint.y - containerRect.top
        : containerRect.height / 2;

      const imageX = (mx - panOffset.x) / zoomLevel;
      const imageY = (my - panOffset.y) / zoomLevel;

      const newPanX = mx - imageX * clampedZoom;
      const newPanY = my - imageY * clampedZoom;

      setZoomLevel(clampedZoom);
      setPanOffset({ x: newPanX, y: newPanY });
    },
    [zoomLevel, panOffset],
  );

  const resetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setSelectedObjects([]);
  };

  const handleContainerMouseMove = (event) => {
    if (isDragging) {
      return;
    }

    if (!planImageLoaded) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    let moveX = event.clientX - containerRect.left;
    let moveY = event.clientY - containerRect.top;

    moveX = (moveX - panOffset.x) / zoomLevel;
    moveY = (moveY - panOffset.y) / zoomLevel;

    const isOverObject = detectedObjects.some((obj) => {
      const x = obj.position.x;
      const y = obj.position.y;
      const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 3) : 40;
      const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 3) : 40;

      return (
        moveX >= x && moveX <= x + width && moveY >= y && moveY <= y + height
      );
    });

    if (isOverObject !== isHoveringObject) {
      setIsHoveringObject(isOverObject);
    }
  };

  const handleMouseDown = (event) => {
    // Handle both mouse clicks and trackpad clicks
    if (event.button === 0) {
      // Left mouse button or trackpad click
      setIsDragging(true);
      setDragStart({
        x: event.clientX - panOffset.x,
        y: event.clientY - panOffset.y,
      });
      dragStartCoords.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseMove = useCallback(
    (event) => {
      if (isDragging) {
        const newPanOffset = {
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        };
        setPanOffset(newPanOffset);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(
    (event) => {
      if (isDragging) {
        setIsDragging(false);

        const dx = Math.abs(event.clientX - dragStartCoords.current.x);
        const dy = Math.abs(event.clientY - dragStartCoords.current.y);

        if (dx < 5 && dy < 5) {
          handleObjectClick(event);
        }

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, handleObjectClick],
  );

  const handleMouseLeave = () => {
    // Stop dragging if mouse leaves the container
    setIsDragging(false);
  };

  // Mobile touch event handlers
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const x = (touches[0].clientX + touches[1].clientX) / 2;
    const y = (touches[0].clientY + touches[1].clientY) / 2;
    return { x, y };
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    if (event.touches.length === 1) {
      // Single finger - start panning
      setIsTouching(true);
      const touch = event.touches[0];
      setTouchStart({
        x: touch.clientX - panOffset.x,
        y: touch.clientY - panOffset.y,
      });
      touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
    } else if (event.touches.length === 2) {
      // Two fingers - start pinch zoom
      setIsTouching(false); // Stop panning
      const distance = getTouchDistance(event.touches);
      setInitialPinchDistance(distance);
      setInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = useCallback(
    (event) => {
      event.preventDefault();
      if (event.touches.length === 1 && isTouching) {
        // Single finger - pan
        const touch = event.touches[0];
        const newPanOffset = {
          x: touch.clientX - touchStart.x,
          y: touch.clientY - touchStart.y,
        };
        setPanOffset(newPanOffset);
      } else if (event.touches.length === 2) {
        // Two fingers - pinch zoom
        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);

        if (initialPinchDistance > 0) {
          const scale = distance / initialPinchDistance;
          const newZoom = Math.max(0.1, Math.min(5, initialZoom * scale));

          // Calculate zoom with center point
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const mousePosition = {
              x: center.x - containerRect.left,
              y: center.y - containerRect.top,
            };

            // Apply zoom centered on pinch point
            const zoomFactor = newZoom / zoomLevel;
            const newPanX =
              mousePosition.x - (mousePosition.x - panOffset.x) * zoomFactor;
            const newPanY =
              mousePosition.y - (mousePosition.y - panOffset.y) * zoomFactor;

            setZoomLevel(newZoom);
            setPanOffset({ x: newPanX, y: newPanY });
          }
        }
      }
    },
    [
      isTouching,
      touchStart,
      initialPinchDistance,
      initialZoom,
      zoomLevel,
      panOffset,
    ],
  );

  const handleTouchEnd = useCallback(
    (event) => {
      if (event.touches.length === 0) {
        // All fingers lifted
        if (isTouching) {
          setIsTouching(false);

          // Check if it was a tap (minimal movement)
          const touch = event.changedTouches[0];
          const dx = Math.abs(touch.clientX - touchStartCoords.current.x);
          const dy = Math.abs(touch.clientY - touchStartCoords.current.y);

          if (dx < 10 && dy < 10) {
            // Simulate click for object selection
            const syntheticEvent = {
              clientX: touch.clientX,
              clientY: touch.clientY,
              preventDefault: () => {},
              stopPropagation: () => {},
            };
            handleObjectClick(syntheticEvent);
          }
        }

        // Reset pinch zoom state
        setInitialPinchDistance(0);
        setInitialZoom(1);
      } else if (event.touches.length === 1) {
        // One finger remaining, restart single touch
        setInitialPinchDistance(0);
        if (!isTouching) {
          const touch = event.touches[0];
          setIsTouching(true);
          setTouchStart({
            x: touch.clientX - panOffset.x,
            y: touch.clientY - panOffset.y,
          });
          touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
        }
      }
    },
    [isTouching, handleObjectClick, panOffset],
  );

  // Handle wheel zoom
  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();
      handleZoom(event);
    },
    [handleZoom],
  );

  // Add wheel event listener to container
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        container.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel]);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  // Add global touch event listeners for touch interactions
  useEffect(() => {
    if (isTouching) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouching, touchStart]);

  const getObjectStats = () => {
    const total = detectedObjects.length;
    const installed = detectedObjects.filter(
      (obj) => obj.status === "installed",
    ).length;
    const uninstalled = detectedObjects.filter(
      (obj) => obj.status === "uninstalled",
    ).length;
    const plan_vs_real_dev = detectedObjects.filter(
      (obj) => obj.status === "plan_vs_real_dev",
    ).length;
    const real_vs_plan_dev = detectedObjects.filter(
      (obj) => obj.status === "real_vs_plan_dev",
    ).length;
    const occluded = detectedObjects.filter(
      (obj) => obj.status === "occluded",
    ).length;

    return {
      total,
      installed,
      uninstalled,
      plan_vs_real_dev,
      real_vs_plan_dev,
      occluded,
    };
  };

  const stats = getObjectStats();

  const handleStatusChange = (status) => {
    handleClearInProgressDraw();
    setShouldFlash(true);

    let statuses;
    if (status === null) {
      statuses =
        selectedStatuses.length === Object.keys(STATUSES).length
          ? []
          : Object.keys(STATUSES);
    } else {
      statuses = selectedStatuses.includes(status)
        ? selectedStatuses.filter((s) => s !== status)
        : [...selectedStatuses, status];
    }
    setSelectedStatuses(statuses);
    setSelectedTypes([]);
  };

  const groupedObjects = useMemo(() => {
    return detectedObjects
      .filter((obj) => selectedStatuses.includes(obj.status))
      .reduce((acc, obj) => {
        if (!acc[obj.group_id]) acc[obj.group_id] = [];
        acc[obj.group_id].push(obj);
        return acc;
      }, {});
  }, [detectedObjects, selectedStatuses]);

  useEffect(() => {
    setSelectedTypes(
      Object.keys(groupedObjects).map((group_id) => group_id.toString()),
    );
  }, [groupedObjects]);

  useEffect(() => {
    setSelectedStatuses(Object.keys(STATUSES));
  }, [detectedObjects]);

  const handleTypeChange = (group_id) => {
    handleClearInProgressDraw();
    setShouldFlash(true);

    let types;
    if (group_id === null) {
      types =
        selectedTypes.length === Object.keys(groupedObjects).length
          ? []
          : Object.keys(groupedObjects);
    } else {
      types = selectedTypes.includes(group_id)
        ? selectedTypes.filter((t) => t !== group_id)
        : [...selectedTypes, group_id];
    }
    setSelectedTypes(types);
  };

  const filteredObjectIdsRef = useRef(filteredObjectIds);
  filteredObjectIdsRef.current = filteredObjectIds;

  const handleUpdateObjectStatus = async (status, isCableInstalled) => {
    if (!isObjectCreationMode || !addedBboxInfo || !selectedObject?._id) {
      console.error("Missing required data for object update");
      setError("Missing required data for object update");
      return;
    }

    // We need documentationId and frameId to call the API
    // These should be provided from the parent component or derived from addedBboxInfo
    if (!addedBboxInfo.documentationId || !addedBboxInfo.frameId) {
      console.error("Missing documentationId or frameId in addedBboxInfo");
      setError("Missing documentation or frame information");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData = {
        bndbox: addedBboxInfo.bbox,
        detectedObjectId: selectedObject._id,
        isDeviation: selectedObject.status === "real_vs_plan_dev",
        status: status,
        ...(isCableInstalled && { isCableInstalled: true }),
      };

      await api.addFrameObjectToDocumentation(
        addedBboxInfo.documentationId,
        addedBboxInfo.frameId,
        requestData,
      );

      onDoneObjectUpdate?.();
    } catch (error) {
      console.error("Error updating detected object:", error);
      toast.error(error.message || "Failed to update detected object");
    } finally {
      setLoading(false);
      setError(null);
    }
  };

  const handleMarkCableInstalled = async () => {
    if (!isObjectCreationMode || !addedBboxInfo || !selectedObject?._id) {
      console.error("Missing required data for cable-only marking");
      toast.error("Missing required data for cable-only marking");
      return;
    }

    if (!addedBboxInfo.documentationId || !addedBboxInfo.frameId) {
      console.error("Missing documentationId or frameId in addedBboxInfo");
      toast.error("Missing documentation or frame information");
      return;
    }

    setLoading(true);

    try {
      await api.addCableOnlyToDocumentation(
        addedBboxInfo.documentationId,
        addedBboxInfo.frameId,
        {
          floorPlanElementId: selectedObject._id,
        },
      );

      onDoneObjectUpdate?.();
    } catch (error) {
      console.error("Error marking cable only:", error);
      toast.error(error.message || "Failed to mark cable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "w" || event.key === "W") {
        if (user?.isGlobalAdmin) {
          setIsEditMode(true);
        }
      }
      if (event.key === "Escape" || event.key === "Esc") {
        if (createObjectModal.isOpen) {
          setCreateObjectModal({ isOpen: false, data: null });
          return;
        }
        if (deleteModalOpen) {
          setDeleteModalOpen(false);
          return;
        }
        if (deleteFloorPlanElementModalOpen) {
          setDeleteFloorPlanElementModalOpen(false);
          return;
        }
        setIsEditMode(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    createObjectModal.isOpen,
    deleteModalOpen,
    deleteFloorPlanElementModalOpen,
    user?.isGlobalAdmin,
  ]);

  const handleCreateObject = async (objectData: any) => {
    if (!sectionData?._id || !projectId) {
      toast.error("Missing section or project information");
      return;
    }

    if (objectData.status !== "uninstalled" && !addedBboxInfo) {
      toast.error("Missing bounding box information");
      return;
    }

    setLoading(true);

    try {
      const response = await api.createDetectedObject(
        projectId,
        sectionData._id,
        { ...objectData, floorPlanId: sectionData?.plan?.id },
      );

      if (response.success) {
        if (
          isObjectCreationMode &&
          addedBboxInfo?.bbox &&
          addedBboxInfo?.documentationId &&
          addedBboxInfo?.frameId
        ) {
          const requestData = {
            bndbox: addedBboxInfo.bbox,
            detectedObjectId: response.data._id,
            isDeviation: objectData.status === "real_vs_plan_dev",
            status: objectData.status,
          };

          try {
            await api.addFrameObjectToDocumentation(
              addedBboxInfo.documentationId,
              addedBboxInfo.frameId,
              requestData,
            );
            onDoneObjectUpdate?.();
          } catch (error) {
            throw new Error(
              error.message || "Failed to add frame object to documentation",
            );
          }
        }

        // Refresh the detected objects list
        if (!addedBboxInfo?.documentationId) {
          return;
        }

        const objectsResponse = await api.fetchDetectedObjectsByDocumentation(
          addedBboxInfo.documentationId,
        );

        if (objectsResponse.success) {
          setDetectedObjects(objectsResponse.data || []);
        }

        // Close modal and select the new object
        setCreateObjectModal({ isOpen: false, data: null });
        setSelectedObjects([response.data]);
      }
    } catch (error) {
      console.error("Error creating detected object:", error);
      toast.error(error.message || "Failed to create detected object");
    } finally {
      setLoading(false);
    }
  };

  const handleDoneMerging = async () => {
    if (!selectedObjects.length || !sectionData?._id || !projectId) return;
    setIsMergingMode(false);
    try {
      if (selectedObjects.some((obj) => obj.status !== "uninstalled")) {
        toast.error("Please select only uninstalled objects for merging");
        return;
      }
      const primaryId = selectedObjects[0]._id;
      const mergeIds = selectedObjects.slice(1).map((o) => o._id);
      if (mergeIds.length === 0) return;

      setLoading(true);
      console.log("sectionData", sectionData);

      await api.mergeDetectedObjects(
        projectId,
        sectionData._id,
        primaryId,
        mergeIds,
        sectionData?.plan?.id,
      );

      if (!addedBboxInfo?.documentationId) {
        return;
      }

      const objectsResponse = await api.fetchDetectedObjectsByDocumentation(
        addedBboxInfo.documentationId,
      );

      if (objectsResponse.success) {
        setDetectedObjects(objectsResponse.data || []);
      }
      setSelectedObjects([]);
    } catch (error) {
      console.error("Error merging detected objects:", error);
      setError(error.message || "Failed to merge detected objects");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteObject = async () => {
    if (!selectedObject?._id || !sectionData?._id || !projectId) return;
    setDeleteModalOpen(true);
  };

  const handleDeleteFloorPlanElement = async () => {
    if (!selectedObject?._id || !sectionData?.plan?.id) return;
    setDeleteFloorPlanElementModalOpen(true);
  };

  const confirmDeleteObject = async () => {
    if (!selectedObject?._id || !sectionData?._id || !projectId) return;
    setIsDeleting(true);
    setError(null);
    try {
      await api.deleteDetectedObject(
        projectId,
        sectionData._id,
        selectedObject._id,
        selectedObject.status === "real_vs_plan_dev",
      );
      if (!addedBboxInfo?.documentationId) {
        return;
      }
      const objectsResponse = await api.fetchDetectedObjectsByDocumentation(
        addedBboxInfo.documentationId,
      );
      if (objectsResponse.success) {
        setDetectedObjects(objectsResponse.data || []);
      }
      setSelectedObjects([]);
      setDeleteModalOpen(false);
      onDoneObjectUpdate?.();
    } catch (error) {
      console.error("Error deleting detected object:", error);
      toast.error(error.message || "Failed to delete detected object");
    } finally {
      setIsDeleting(false);
      setError(null);
    }
  };

  const confirmDeleteFloorPlanElement = async () => {
    if (!selectedObject?._id || !sectionData?.plan?.id) return;
    setIsDeletingFloorPlanElement(true);
    setError(null);
    try {
      await api.deleteFloorPlanElement(sectionData.plan.id, selectedObject._id);
      setSelectedObjects([]);
      setDeleteFloorPlanElementModalOpen(false);

      if (!addedBboxInfo?.documentationId) {
        return;
      }
      const objectsResponse = await api.fetchDetectedObjectsByDocumentation(
        addedBboxInfo.documentationId,
      );
      if (objectsResponse.success) {
        setDetectedObjects(objectsResponse.data || []);
      }
      onDoneObjectUpdate?.();
    } catch (error) {
      console.error("Error deleting floor plan element:", error);
      toast.error(error.message || "Failed to delete floor plan element");
    } finally {
      setIsDeletingFloorPlanElement(false);
      setError(null);
    }
  };

  const handleDeleteCable = (cable: any) => {
    if (!cable || !addedBboxInfo?.documentationId) return;
    setSelectedCableForDeletion(cable);
    setDeleteCableModalOpen(true);
  };

  const confirmDeleteCable = async () => {
    if (
      !selectedCableForDeletion ||
      !addedBboxInfo?.documentationId ||
      !addedBboxInfo?.frameId
    ) {
      return;
    }

    const floorPlanElementId = selectedCableForDeletion.id;

    if (!floorPlanElementId) {
      return;
    }

    setIsDeletingCable(true);
    setError(null);

    try {
      await api.deleteCable(
        addedBboxInfo.documentationId,
        addedBboxInfo.frameId,
        floorPlanElementId,
      );

      setDeleteCableModalOpen(false);
      setSelectedCableForDeletion(null);

      // Refresh orphaned cables or trigger parent component refresh
      onDoneObjectUpdate?.();
    } catch (error) {
      console.error("Error deleting cable:", error);
      toast.error(error.message || "Failed to delete cable");
    } finally {
      setIsDeletingCable(false);
    }
  };

  const handleCloseCreateModal = () => {
    setCreateObjectModal({ isOpen: false, data: null });
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b bg-gray-50">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t("progress_report.title", { sectionName: sectionData.name })}
              </h3>
            </div>

            <div className="flex items-center space-x-2">
              {/* Controls */}
              {!isViewMode && (
                <GlobalAdminGuard>
                  <button
                    onClick={() => setIsEditMode(!isEditMode)}
                    className={`p-2 rounded ${isEditMode ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"} hover:bg-blue-200`}
                    title={
                      isEditMode ? "Turn off Edit Mode" : "Turn on Edit Mode"
                    }
                  >
                    <SquarePen size={16} />
                  </button>
                </GlobalAdminGuard>
              )}

              {!isViewMode && (
                <button
                  onClick={() => setShowObjectLabels(!showObjectLabels)}
                  className={`p-2 rounded ${
                    showObjectLabels
                      ? "bg-blue-100 text-blue-600"
                      : "bg-gray-100 text-gray-600"
                  } hover:bg-blue-200`}
                >
                  <Eye size={16} />
                </button>
              )}

              <button
                onClick={() => handleZoom("out")}
                className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                title={t("progress_report.zoom_out")}
                disabled={zoomLevel <= 0.01}
              >
                <ZoomOut size={16} />
              </button>

              <button
                onClick={() => handleZoom("in")}
                className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                title={t("progress_report.zoom_in")}
                disabled={zoomLevel >= 3}
              >
                <ZoomIn size={16} />
              </button>

              {!isViewMode && (
                <button
                  onClick={resetView}
                  className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  title={t("progress_report.reset_view")}
                >
                  <RotateCcw size={16} />
                </button>
              )}

              {!isViewMode && (
                <button
                  onClick={onClose}
                  className="p-2 text-gray-500 hover:text-gray-700"
                  title={t("progress_report.close")}
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Plan View */}
            <div
              className="flex-1 relative bg-gray-100 overflow-hidden"
              ref={containerRef}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleContainerMouseMove}
              style={{
                cursor: isHoveringObject
                  ? "pointer"
                  : isDragging || isTouching
                    ? "grabbing"
                    : "grab",
                touchAction: "none", // Prevent default touch behaviors
              }}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-600">
                      {t("progress_report.loading_objects")}
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="absolute inset-0 flex items-center justify-center text-red-500 z-10">
                  <p>{t("progress_report.loading_error", { error })}</p>
                </div>
              )}

              {/* Plan Image */}
              <div
                className="relative w-full h-full"
                style={{ overflow: "hidden" }}
              >
                {sectionData.plan?.previewUrl ? (
                  <div
                    className="relative w-full h-full"
                    style={{
                      transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                      transformOrigin: "top left",
                      willChange: "transform",
                    }}
                  >
                    <img
                      ref={planImageRef}
                      src={sectionData.plan.previewUrl}
                      alt={t("progress_report.plan_alt", {
                        sectionName: sectionData.name,
                      })}
                      className="max-w-none select-none"
                      onLoad={() => setPlanImageLoaded(true)}
                      onError={() =>
                        setError(t("progress_report.image_load_error"))
                      }
                      draggable={false}
                    />

                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <Settings size={48} className="mx-auto mb-2" />
                      <p>{t("progress_report.no_plan_available")}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Object Overlay Canvas - sized to container, not image */}
              <canvas
                ref={canvasRef}
                className="absolute top-0 left-0 pointer-events-none"
              />
            </div>

            {/* Side Panel */}
            {!isViewMode && (
              <div className="w-80 border-l bg-white flex flex-col">
                {/* Object Details */}
                {selectedObject ? (
                  <div className="p-4 border-b">
                    <h4 className="font-medium text-gray-900 mb-2">
                      {t("progress_report.object_details")}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">
                          {t("progress_report.type")}:
                        </span>{" "}
                        {selectedObject.group_id}
                      </div>
                      <div>
                        <span className="font-medium">
                          {t("progress_report.group")}:
                        </span>{" "}
                        {selectedObject.group_id}
                      </div>
                      <div>
                        <span className="font-medium">
                          {t("progress_report.status")}:
                        </span>
                        <span
                          className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                            selectedObject.status === "installed"
                              ? "bg-green-100 text-green-800"
                              : selectedObject.status === "uninstalled"
                                ? "bg-orange-100 text-orange-800"
                                : selectedObject.status === "real_vs_plan_dev"
                                  ? "bg-red-100 text-red-800"
                                  : selectedObject.status === "occluded"
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {selectedObject.status === "real_vs_plan_dev"
                            ? t("progress_report.real_vs_plan_deviation")
                            : selectedObject.status}
                        </span>
                      </div>
                      {selectedObject.cableStatus && (
                        <div>
                          <span className="font-medium">
                            {t("progress_report.cable_status", {
                              defaultValue: "Cable status",
                            })}
                            :
                          </span>
                          <span
                            className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                              selectedObject.cableStatus === "installed"
                                ? "bg-green-100 text-green-800"
                                : selectedObject.cableStatus === "uninstalled"
                                  ? "bg-orange-100 text-orange-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {selectedObject.cableStatus}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium">
                          {t("progress_report.position")}:
                        </span>{" "}
                        ({selectedObject.position.x},{" "}
                        {selectedObject.position.y})
                      </div>
                      <div>
                        <span className="font-medium">
                          {t("progress_report.detection_date")}:
                        </span>{" "}
                        {dayjs
                          .utc(selectedObject.detectionDate)
                          .format("YYYY-MM-DD HH:mm")}
                      </div>
                      <div>
                        <span className="font-medium">
                          {t("progress_report.description")}:
                        </span>{" "}
                        {selectedObject.description}
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {selectedObject &&
                        selectedObject.documentations &&
                        selectedObject.documentations.length > 0 &&
                        onOpenDocumentation &&
                        !isObjectCreationMode && (
                          <button
                            onClick={() => {
                              const lastDocumentation =
                                selectedObject.documentations[
                                  selectedObject.documentations.length - 1
                                ];
                              const lastReferenceFrame =
                                lastDocumentation.referenceFrames[
                                  lastDocumentation.referenceFrames.length - 1
                                ];
                              onOpenDocumentation(
                                lastReferenceFrame + ".jpg",
                                selectedObject._id,
                                lastDocumentation.documentationId,
                              );
                            }}
                            className="w-full px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center justify-center"
                          >
                            <svg
                              className="w-4 h-4 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            {t("progress_report.see_in_documentation")}
                          </button>
                        )}

                      {/* Delete object */}
                      <GlobalAdminGuard>
                        <div className="w-full flex flex-col gap-2">
                          {selectedObject.status !== "real_vs_plan_dev" && (
                            <button
                              onClick={handleDeleteFloorPlanElement}
                              className="w-full px-3 py-1 text-sm bg-red-700 text-white rounded hover:bg-red-800 disabled:opacity-50"
                              disabled={isDeletingFloorPlanElement}
                            >
                              {isDeletingFloorPlanElement
                                ? t("progress_report.deleting")
                                : t(
                                    "progress_report.delete_floor_plan_element",
                                    {
                                      defaultValue: "Delete floor plan element",
                                    },
                                  )}
                            </button>
                          )}
                          {selectedObject.status !== "uninstalled" && (
                            <button
                              onClick={handleDeleteObject}
                              className="w-full px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                              disabled={isDeleting}
                            >
                              {isDeleting
                                ? t("progress_report.deleting")
                                : t("progress_report.delete_detected_object", {
                                    defaultValue: "Delete detected object",
                                  })}
                            </button>
                          )}
                        </div>
                        {selectedObject.status === "uninstalled" && (
                          <div className="flex flex-row space-x-2">
                            <button
                              onClick={() => {
                                setIsMergingMode(!isMergingMode);
                                if (isMergingMode) {
                                  setSelectedObjects([]);
                                }
                              }}
                              className="w-full flex-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              disabled={false}
                            >
                              {isMergingMode
                                ? t("progress_report.stop_merging")
                                : t("progress_report.start_merging")}
                            </button>
                            {isMergingMode && (
                              <button
                                onClick={() => handleDoneMerging()}
                                className="w-full flex-1 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                disabled={false}
                              >
                                {t("progress_report.merge_done")}
                              </button>
                            )}
                          </div>
                        )}
                      </GlobalAdminGuard>
                      {isObjectCreationMode &&
                        addedBboxInfo &&
                        selectedObject && (
                          <>
                            <div className="text-xs text-gray-600 mb-2 p-2 bg-blue-50 rounded">
                              {t("progress_report.update_object_status_info")}
                            </div>
                            {selectedObject.status !== "installed" && (
                              <button
                                onClick={() =>
                                  setCableInstallModal({
                                    isOpen: true,
                                    status: "installed",
                                  })
                                }
                                className="w-full px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                disabled={loading}
                              >
                                {loading
                                  ? t("progress_report.updating")
                                  : t("progress_report.mark_as_installed")}
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleUpdateObjectStatus("real_vs_plan_dev")
                              }
                              className="w-full px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                              disabled={loading}
                            >
                              {loading
                                ? t("progress_report.updating")
                                : t("progress_report.mark_as_deviation")}
                            </button>
                            <div className="w-full flex flex-col gap-2">
                              {selectedObject.cableStatus !== "installed" && (
                                <button
                                  onClick={handleMarkCableInstalled}
                                  className="w-full px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                  disabled={loading}
                                >
                                  {loading
                                    ? t("progress_report.updating")
                                    : t(
                                        "progress_report.mark_cable_installed",
                                        {
                                          defaultValue:
                                            "Mark Cable as Installed",
                                        },
                                      )}
                                </button>
                              )}
                              {selectedObject.cableStatus !== "uninstalled" && (
                                <button
                                  onClick={() =>
                                    handleDeleteCable(selectedObject)
                                  }
                                  className="w-full px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                  disabled={isDeletingCable}
                                >
                                  {isDeletingCable
                                    ? t("progress_report.deleting")
                                    : t(
                                        "progress_report.mark_cable_uninstalled",
                                        {
                                          defaultValue:
                                            "Mark Cable as Uninstalled",
                                        },
                                      )}
                                </button>
                              )}
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-b text-center text-gray-500">
                    <Settings size={24} className="mx-auto mb-2" />
                    <p className="text-sm">
                      {t("progress_report.click_object_for_details")}
                    </p>
                  </div>
                )}

                {/* Objects List */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-4">
                    <div className="flex flex-col items-center space-x-4 text-md text-gray-600 mt-1">
                      {[
                        {
                          key: null,
                          label: t("progress_report.total_objects"),
                          colorClass:
                            "text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm mb-2",
                          stat: stats.total,
                        },
                        {
                          key: STATUSES.installed,
                          label: t("progress_report.installed"),
                          colorClass:
                            "text-green-600 bg-green-50 hover:bg-green-100",
                          stat: stats.installed,
                        },
                        {
                          key: STATUSES.uninstalled,
                          label: t("progress_report.not_installed"),
                          colorClass:
                            "text-orange-600 bg-orange-50 hover:bg-orange-100",
                          stat: stats.uninstalled,
                        },
                        {
                          key: STATUSES.real_vs_plan_dev,
                          label: t("progress_report.real_vs_plan_dev"),
                          colorClass: "text-red-600 bg-red-50 hover:bg-red-100",
                          stat: stats.real_vs_plan_dev,
                        },
                        {
                          key: STATUSES.occluded,
                          label: t("progress_report.occluded"),
                          colorClass:
                            "text-amber-600 bg-amber-50 hover:bg-amber-100",
                          stat: stats.occluded,
                        },
                      ].map(({ key, label, colorClass, stat }) => (
                        <label
                          key={label}
                          className={`flex items-center space-x-1 cursor-pointer select-none ${colorClass} px-2 py-1 rounded transition-colors w-full`}
                        >
                          <input
                            type="checkbox"
                            checked={
                              key === null
                                ? selectedStatuses.length ===
                                  Object.keys(STATUSES).length
                                : selectedStatuses.includes(key)
                            }
                            onChange={() => handleStatusChange(key)}
                            className="form-checkbox accent-blue-600"
                          />
                          <span>
                            {label}: ({stat})
                          </span>
                        </label>
                      ))}
                    </div>
                    {Object.keys(groupedObjects).length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 mt-2">
                          {t("progress_report.detected_object_groups")}
                        </h4>
                        <div className="flex flex-col items-center space-x-4 text-sm text-gray-600 mt-1">
                          {[
                            {
                              key: null,
                              label: t("progress_report.all_types"),
                              colorClass:
                                "text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm mb-2",
                              stat: Object.values(groupedObjects).reduce(
                                (sum, arr) => sum + arr.length,
                                0,
                              ),
                            },
                            ...Object.keys(groupedObjects).map((type) => ({
                              key: type,
                              label: type,
                              colorClass:
                                "text-blue-600 bg-blue-50 hover:bg-blue-100",
                              stat: groupedObjects[type].length,
                            })),
                          ].map(({ key, label, colorClass, stat }) => (
                            <label
                              key={label}
                              className={`flex items-center space-x-1 cursor-pointer select-none ${colorClass} px-2 py-1 rounded transition-colors w-full`}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  key === null
                                    ? selectedTypes.length ===
                                      Object.keys(groupedObjects).length
                                    : selectedTypes.includes(key)
                                }
                                onChange={() => handleTypeChange(key)}
                                className="form-checkbox accent-blue-600"
                              />
                              <span>
                                {label}: ({stat})
                              </span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Legend */}
                <div className="p-4 border-t bg-gray-50">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    {t("progress_report.legend")}
                  </h5>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                      <span>{t("progress_report.installed")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded mr-2"></div>
                      <span>{t("progress_report.uninstalled")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                      <span>{t("progress_report.real_vs_plan_deviation")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-amber-500 rounded mr-2"></div>
                      <span>{t("progress_report.occluded")}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {createObjectModal.isOpen && (
        <CreateObjectModal
          onClose={handleCloseCreateModal}
          onCreateObject={handleCreateObject}
          data={createObjectModal.data}
          isLoading={loading}
        />
      )}

      {/* Cable Installation Confirmation Modal */}
      {cableInstallModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 relative">
            <button
              onClick={() =>
                setCableInstallModal({ isOpen: false, status: null })
              }
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              title={t("common.close", { defaultValue: "Close" })}
            >
              <X size={20} />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pr-8">
              {t("progress_report.cable_installation_question", {
                defaultValue: "Is the cable installed?",
              })}
            </h3>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setCableInstallModal({ isOpen: false, status: null });
                  handleUpdateObjectStatus(cableInstallModal.status);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                {t("common.no", { defaultValue: "No" })}
              </button>
              <button
                onClick={() => {
                  setCableInstallModal({ isOpen: false, status: null });
                  handleUpdateObjectStatus(cableInstallModal.status, true);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                {t("common.yes", { defaultValue: "Yes" })}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={deleteFloorPlanElementModalOpen}
        title={t("progress_report.delete_floor_plan_element", {
          defaultValue: "Delete floor plan element",
        })}
        message={t("progress_report.delete_floor_plan_element_message", {
          defaultValue:
            "This will permanently remove the floor plan element. Are you sure?",
        })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={confirmDeleteFloorPlanElement}
        onCancel={() => setDeleteFloorPlanElementModalOpen(false)}
        isProcessing={isDeletingFloorPlanElement}
        variant="danger"
      />
      <ConfirmModal
        isOpen={deleteModalOpen}
        title={t("progress_report.delete_object_title")}
        message={t("progress_report.delete_object_message")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={confirmDeleteObject}
        onCancel={() => setDeleteModalOpen(false)}
        isProcessing={isDeleting}
        variant="danger"
      />
      <ConfirmModal
        isOpen={deleteCableModalOpen}
        title={t("progress_report.mark_cable_uninstalled")}
        message={t("progress_report.mark_cable_uninstalled_message")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={confirmDeleteCable}
        onCancel={() => {
          setDeleteCableModalOpen(false);
          setSelectedCableForDeletion(null);
        }}
        isProcessing={isDeletingCable}
        variant="danger"
      />
    </>
  );
};

export default ProgressReportModalViewer;
